import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Autocomplete,
  InputAdornment,
  Chip,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SpeedIcon from '@mui/icons-material/Speed';
import PageLayout from '../common/components/PageLayout';
import ManagementMenu from './ManagementMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  tablePaper: {
    marginTop: theme.spacing(2),
  },
  speedExceeded: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  statsChips: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
}));

const SpeedingPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Fix para ícone do calendário no dark theme
  const dateTimeFieldSx = {
    minWidth: 180,
    '& input[type="datetime-local"]': {
      colorScheme: isDarkMode ? 'dark' : 'light',
    },
    '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      filter: isDarkMode ? 'invert(1)' : 'none',
      opacity: isDarkMode ? 0.8 : 1,
    },
  };

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [speedLimit, setSpeedLimit] = useState(80);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 16);
  });
  const [loading, setLoading] = useState(false);
  const [violations, setViolations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffectAsync(async () => {
    const response = await fetch('/api/devices');
    if (response.ok) {
      setDevices(await response.json());
    }
  }, []);

  const handleSearch = async () => {
    if (!selectedDevice) return;

    setLoading(true);
    setViolations([]);

    try {
      const params = new URLSearchParams({
        deviceId: selectedDevice.id,
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo).toISOString(),
      });

      const response = await fetch(`/api/positions?${params}`);
      if (response.ok) {
        const positions = await response.json();

        const speedViolations = positions
          .filter((pos) => pos.speed * 1.852 > speedLimit)
          .map((pos) => ({
            id: pos.id,
            fixTime: pos.fixTime,
            speed: Math.round(pos.speed * 1.852),
            address: pos.address || t('sharedNoAddress'),
            latitude: pos.latitude,
            longitude: pos.longitude,
            exceeded: Math.round(pos.speed * 1.852 - speedLimit),
          }));

        setViolations(speedViolations);
        setPage(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (violations.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Infrações de Velocidade');

    worksheet.columns = [
      { header: 'Data/Hora', key: 'fixTime', width: 20 },
      { header: 'Velocidade (km/h)', key: 'speed', width: 18 },
      { header: 'Limite (km/h)', key: 'limit', width: 15 },
      { header: 'Excesso (km/h)', key: 'exceeded', width: 15 },
      { header: 'Endereço', key: 'address', width: 50 },
    ];

    violations.forEach((v) => {
      worksheet.addRow({
        fixTime: new Date(v.fixTime).toLocaleString(),
        speed: v.speed,
        limit: speedLimit,
        exceeded: v.exceeded,
        address: v.address,
      });
    });

    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `velocidade_${selectedDevice?.name}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    if (violations.length === 0) return null;

    const speeds = violations.map((v) => v.speed);
    const maxSpeed = Math.max(...speeds);
    const avgSpeed = Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length);
    const maxExceeded = Math.max(...violations.map((v) => v.exceeded));

    return { total: violations.length, maxSpeed, avgSpeed, maxExceeded };
  }, [violations]);

  const paginatedViolations = useMemo(() => {
    return violations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [violations, page, rowsPerPage]);

  return (
    <PageLayout menu={<ManagementMenu />} breadcrumbs={['reportManagement', 'reportSpeeding']}>
      <Container maxWidth={false} className={classes.container}>
        {/* Filters */}
        <Paper className={classes.filterPaper}>
          <Typography variant="h6" gutterBottom>
            <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('reportSpeeding')}
          </Typography>

          <Box className={classes.filterRow}>
            <Autocomplete
              size="small"
              options={devices}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={selectedDevice}
              onChange={(e, value) => setSelectedDevice(value)}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <li {...rest} key={option.id}>
                    {option.name}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('deviceTitle')} sx={{ minWidth: 200 }} />
              )}
            />

            <TextField
              size="small"
              type="datetime-local"
              label={t('reportFrom')}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateTimeFieldSx}
            />

            <TextField
              size="small"
              type="datetime-local"
              label={t('reportTo')}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateTimeFieldSx}
            />

            <TextField
              size="small"
              type="number"
              label={t('speedLimit')}
              value={speedLimit}
              onChange={(e) => setSpeedLimit(Number(e.target.value))}
              InputProps={{
                endAdornment: <InputAdornment position="end">km/h</InputAdornment>,
              }}
              sx={{ width: 130 }}
            />

            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!selectedDevice || loading}
            >
              {t('reportShow')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={violations.length === 0}
            >
              {t('reportExport')}
            </Button>
          </Box>

          {stats && (
            <Box className={classes.statsChips}>
              <Chip
                label={`${stats.total} infrações`}
                color="error"
                variant="outlined"
              />
              <Chip
                label={`Máx: ${stats.maxSpeed} km/h`}
                color="warning"
                variant="outlined"
              />
              <Chip
                label={`Média: ${stats.avgSpeed} km/h`}
                color="info"
                variant="outlined"
              />
              <Chip
                label={`Excesso máx: +${stats.maxExceeded} km/h`}
                color="error"
                variant="outlined"
              />
            </Box>
          )}
        </Paper>

        {loading && <LinearProgress />}

        {/* Results Table */}
        {violations.length > 0 ? (
          <Paper className={classes.tablePaper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('positionFixTime')}</TableCell>
                  <TableCell align="right">{t('positionSpeed')}</TableCell>
                  <TableCell align="right">{t('speedLimit')}</TableCell>
                  <TableCell align="right">Excesso</TableCell>
                  <TableCell>{t('positionAddress')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedViolations.map((violation) => (
                  <TableRow key={violation.id}>
                    <TableCell>
                      {new Date(violation.fixTime).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" className={classes.speedExceeded}>
                      {violation.speed} km/h
                    </TableCell>
                    <TableCell align="right">{speedLimit} km/h</TableCell>
                    <TableCell align="right" className={classes.speedExceeded}>
                      +{violation.exceeded} km/h
                    </TableCell>
                    <TableCell>{violation.address}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={violations.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('sharedRowsPerPage')}
            />
          </Paper>
        ) : !loading && selectedDevice ? (
          <Paper className={classes.noData}>
            <SpeedIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
            <Typography variant="h6">{t('sharedNoData')}</Typography>
            <Typography color="textSecondary">
              Nenhuma infração de velocidade encontrada no período selecionado
            </Typography>
          </Paper>
        ) : null}
      </Container>
    </PageLayout>
  );
};

export default SpeedingPage;
